import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./index.less";
import { Link, useHistory, useLocation } from "react-router-dom";
import { BrowserView, isBrowser, MobileView } from "react-device-detect";
import {
  Layout,
  Row,
  Menu,
  Divider,
  Button,
  Typography,
  Dropdown,
  Col,
} from "antd";
import {
  UserOutlined,
  ShoppingOutlined,
  MenuOutlined,
  LeftOutlined,
  DownOutlined,
} from "@ant-design/icons";
import I18n from "../../i18n";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import AppDrawer from "./components/AppDrawer";
import { loadCategories } from "../../../application/categories-slice";
import { loadCartItems } from "../../../application/cart-slice";
import useWindowDimensions from "../../hooks/useWindowDimensions";

const { Item, SubMenu } = Menu;

export function Header() {
  const dispatch = useAppDispatch();
  const stableDispatch = useCallback(dispatch, []);
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { loading, generalCategories } = useAppSelector(
    (state) => state.categories
  );
  const { items } = useAppSelector((state) => state.cart);
  const cartItemsCount = items.length;
  const { width } = useWindowDimensions();

  useEffect(() => {
    stableDispatch(loadCartItems());
    stableDispatch(loadCategories());
  }, [stableDispatch]);

  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const openDrawer = () => setIsDrawerVisible(true);
  const closeDrawer = () => setIsDrawerVisible(false);

  const openCategory = (categoryId: string, categoryTitle: string) => {
    history.push(
      `/category/${categoryId}/${encodeURIComponent(categoryTitle)}`
    );
    closeDrawer();
  };

  const renderAllOfThisCategory = () => (
    <Row align="middle" justify="start">
      <Typography.Text>{I18n.drawer.allOfThisCategory}</Typography.Text>
      <i className="ant-menu-submenu-arrow" />
      <LeftOutlined className="all-off-this-category-icon" />
    </Row>
  );

  const renderMenuItems = (openKeys: string[]) => {
    if (loading !== "succeeded") return <Fragment />;
    return generalCategories.map((generalCategory) => {
      if (generalCategory.subCategories.length === 0) {
        return (
          <Item
            key={generalCategory.id}
            onClick={() =>
              openCategory(generalCategory.id, generalCategory.title)
            }
          >
            {generalCategory.title}
          </Item>
        );
      }
      const isActive = openKeys.indexOf(generalCategory.id) !== -1;
      return (
        <SubMenu
          className={
            (isActive ? "drawer-sub-menu-active" : "drawer-sub-menu-inactive") +
            " drawer-sub-menu"
          }
          key={generalCategory.id}
          title={
            <span className={isActive ? "drawer-sub-menu-active-title" : ""}>
              {generalCategory.title}
            </span>
          }
        >
          <Item
            key={`${generalCategory.id}-all`}
            onClick={() =>
              openCategory(generalCategory.id, generalCategory.title)
            }
          >
            {renderAllOfThisCategory()}
          </Item>
          {generalCategory.subCategories.map((subCategory) => {
            if (subCategory.categories.length === 0) {
              return (
                <Item
                  key={`${generalCategory.id}-${subCategory.id}`}
                  onClick={() =>
                    openCategory(
                      `${generalCategory.id}-${subCategory.id}`,
                      subCategory.title
                    )
                  }
                >
                  {subCategory.title}
                </Item>
              );
            }
            const isActive =
              openKeys.indexOf(`${generalCategory.id}-${subCategory.id}`) !==
              -1;
            return (
              <SubMenu
                className={
                  (isActive
                    ? "drawer-sub-menu-active"
                    : "drawer-sub-menu-inactive") + " drawer-sub-menu"
                }
                key={`${generalCategory.id}-${subCategory.id}`}
                title={
                  <span
                    className={isActive ? "drawer-sub-menu-active-title" : ""}
                  >
                    {subCategory.title}
                  </span>
                }
              >
                <Item
                  key={`${generalCategory.id}-${subCategory.id}-all`}
                  onClick={() =>
                    openCategory(
                      `${generalCategory.id}-${subCategory.id}`,
                      subCategory.title
                    )
                  }
                >
                  {renderAllOfThisCategory()}
                </Item>
                {subCategory.categories.map((category) => (
                  <Item
                    key={`${generalCategory.id}-${subCategory.id}-${category.id}`}
                    onClick={() =>
                      openCategory(
                        `${generalCategory.id}-${subCategory.id}-${category.id}`,
                        category.title
                      )
                    }
                  >
                    {category.title}
                  </Item>
                ))}
              </SubMenu>
            );
          })}
        </SubMenu>
      );
    });
  };

  const renderDesktopMenuItems = () => {
    if (loading !== "succeeded") return <Fragment />;
    return generalCategories.map((generalCategory) => {
      if (generalCategory.subCategories.length === 0) {
        return (
          <Button
            className="header-menu-desktop"
            type="text"
            key={generalCategory.id}
            onClick={() =>
              openCategory(generalCategory.id, generalCategory.title)
            }
          >
            {generalCategory.title}
          </Button>
        );
      }
      return (
        <Dropdown
          key={generalCategory.id}
          placement="bottomLeft"
          overlay={
            <div
              className="header-menu-dropdown-overlay"
              style={{
                marginLeft: width > 1366 ? (width - 1366) / 2 : 8,
                width: width > 1366 ? 1366 - 32 : width - 32,
              }}
            >
              <Row>
                {generalCategory.subCategories.map((subCategory) => {
                  return (
                    <Col
                      key={subCategory.id}
                      xs={8}
                      className="header-sub-category-col"
                    >
                      <Row align="middle">
                        <span className="header-sub-category-item-dot" />
                        <Link
                          to={`/category/${generalCategory.id}-${subCategory.id
                            }/${encodeURIComponent(subCategory.title)}`}
                        >
                          <Typography.Text className="header-sub-category-item-title">
                            {subCategory.title}
                          </Typography.Text>
                        </Link>
                      </Row>
                      <div className="header-caregory-list">
                        {subCategory.categories.map((category) => {
                          return (
                            <div
                              key={category.id}
                              className="header-category-item-title"
                            >
                              <Link
                                to={`/category/${generalCategory.id}-${subCategory.id
                                  }-${category.id}/${encodeURIComponent(
                                    category.title
                                  )}`}
                              >
                                <Typography.Text>
                                  {category.title}
                                </Typography.Text>
                              </Link>
                            </div>
                          );
                        })}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          }
        >
          <Button
            type="text"
            className="header-menu-desktop"
            onClick={() =>
              openCategory(generalCategory.id, generalCategory.title)
            }
          >
            {generalCategory.title} <DownOutlined />
          </Button>
        </Dropdown >
      );
    });
  };

  return (
    <Layout.Header className="header">
      <div className="header-body">
        <AppDrawer
          visible={isDrawerVisible}
          onClose={closeDrawer}
          renderMenuItems={renderMenuItems}
        />
        <MobileView>
          <MenuOutlined className="header-drawer-icon" onClick={openDrawer} />
        </MobileView>
        <Link to="/">
          <h1
            className={
              isBrowser ? "header-title-desktop" : "header-title-mobile"
            }
          >
            {I18n.title}
          </h1>
        </Link>
        <Row justify={isBrowser ? "space-between" : "end"}>
          <BrowserView>
            <Menu style={{ backgroundColor: 'transparent' }} mode="horizontal" className="header-nav" selectable={false}>
              {renderDesktopMenuItems()}
            </Menu>
          </BrowserView>
          <div className="header-left-options">
            {isAuthenticated === "authenticated" ? (
              <Link to="/profile/">
                <UserOutlined className="header-profile-icon" />
              </Link>
            ) : (
              <Button
                style={{ backgroundColor: "#d90429", color: 'white', borderWidth: 0, marginLeft: 10 }}
                size="middle"
                className="header-login-button"
                onClick={() =>
                  history.push(`/login-register?back=${location.pathname}`)
                }
              >
                {I18n.loginAuth.title}
              </Button>
            )}
            <Divider type="vertical" className="profile-cart-divider" />
            <Link to="/cart/">
              <div className="header-cart-icon-and-badge-container">
                {cartItemsCount === 0 ? (
                  <Fragment />
                ) : (
                  <p className="header-cart-icon-badge">{cartItemsCount}</p>
                )}
                <ShoppingOutlined className="header-cart-icon" />
              </div>
            </Link>
          </div>
        </Row>
      </div>
    </Layout.Header>
  );
}
